import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Skeleton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import api from '../api/axios';
import { useProject } from '../context/ProjectContext';
import { TEMPLATES, getTemplate } from '../dashboards/templates';

function NewDashboardDialog({ open, onClose, onCreate, creating }) {
  const [name, setName] = useState('');
  const [template, setTemplate] = useState(TEMPLATES[0].id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      TransitionProps={{ onEnter: () => { setName(''); setTemplate(TEMPLATES[0].id); } }}
    >
      <DialogTitle>New dashboard</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Dashboard name" value={name} onChange={(e) => setName(e.target.value)}
            required autoFocus fullWidth size="small" placeholder="e.g. Speedrun leaderboard"
            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onCreate({ name: name.trim(), template }); }}
          />
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1 }}>Template</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            {TEMPLATES.map(({ id, name: title, description, Icon }) => {
              const active = template === id;
              return (
                <Paper key={id} onClick={() => setTemplate(id)}
                  sx={{
                    p: 1.75, borderRadius: 1.5, cursor: 'pointer', border: '1.5px solid',
                    borderColor: active ? 'primary.main' : 'divider',
                    bgcolor: active ? 'rgba(210,122,90,0.08)' : 'transparent',
                    transition: 'all 0.15s', '&:hover': { borderColor: 'primary.light' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Icon sx={{ fontSize: 18, color: active ? 'primary.main' : 'text.secondary' }} />
                    <Typography fontWeight={600} fontSize="0.9rem">{title}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block' }}>
                    {description}
                  </Typography>
                </Paper>
              );
            })}
            <Paper sx={{
              p: 1.75, borderRadius: 1.5, border: '1.5px dashed', borderColor: 'divider',
              display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'transparent',
            }}>
              <Typography variant="caption" color="text.disabled">More templates soon</Typography>
            </Paper>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="text" color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || creating}
          onClick={() => onCreate({ name: name.trim(), template })}
        >
          Create dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DashboardCard({ dashboard, onOpen }) {
  const template = getTemplate(dashboard.template);
  const Icon = template?.Icon;
  const sourceLabel = dashboard.source && template ? template.sourceLabel(dashboard.source) : '';

  return (
    <Paper onClick={onOpen}
      sx={{
        p: 1.75, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', cursor: 'pointer',
        display: 'flex', gap: 1.5, alignItems: 'center', transition: 'all 0.15s',
        '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
      }}
    >
      <Box sx={{
        width: 38, height: 38, borderRadius: 1, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: 'rgba(210,122,90,0.12)', color: 'primary.main',
      }}>
        {Icon && <Icon fontSize="small" />}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography fontWeight={600} noWrap>{dashboard.name}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          {sourceLabel ? <LinkIcon sx={{ fontSize: 13 }} /> : <LinkOffIcon sx={{ fontSize: 13 }} />}
          <Typography variant="caption" noWrap>
            {template?.name || dashboard.template} · {sourceLabel || 'no data source'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

export default function Dashboards() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { projectId, projectName } = useProject();
  const [creating, setCreating] = useState(false);

  const { data: dashboards = [], isLoading } = useQuery({
    queryKey: ['dashboards', projectId],
    queryFn: () => api.get('/dashboards', { params: { projectId } }).then((r) => r.data),
    enabled: !!projectId,
  });

  const createMutation = useMutation({
    mutationFn: (data) => api.post('/dashboards', { ...data, projectId }).then((r) => r.data),
    onSuccess: (dashboard) => {
      qc.invalidateQueries({ queryKey: ['dashboards', projectId] });
      setCreating(false);
      navigate(`/app/dashboard/${dashboard.id}`);
    },
  });

  if (!projectId) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>Select a project to see its dashboards.</Typography>
        <Button variant="contained" onClick={() => navigate('/projects')}>Choose project</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">{projectName}</Typography>
          <Typography variant="h5" lineHeight={1.25}>Dashboards</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          New dashboard
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.5 }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={68} sx={{ borderRadius: 1.5 }} />)}
        </Box>
      ) : dashboards.length === 0 ? (
        <Box sx={{
          p: 5, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5,
        }}>
          <Typography color="text.secondary">No dashboards in this project yet.</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreating(true)}>Create the first one</Button>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.5 }}>
          {dashboards.map((d) => (
            <DashboardCard key={d.id} dashboard={d} onOpen={() => navigate(`/app/dashboard/${d.id}`)} />
          ))}
        </Box>
      )}

      <NewDashboardDialog open={creating} onClose={() => setCreating(false)}
        onCreate={(data) => createMutation.mutate(data)} creating={createMutation.isPending}
      />
    </Box>
  );
}
